import { ReactElement } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { Button, FlexContainer, IconAvatar } from '@act/storybook';
import NotePreviewCard from './NotePreviewCard';

export interface NoteRecoveryDialogProps {
  open: boolean;
  /** Unsaved draft HTML to preview. */
  draftText: string;
  /** Load the draft into the editor and keep editing. */
  onContinue: () => void;
  /** Throw the draft away and start blank. */
  onDiscard: () => void;
}

/**
 * First-step modal shown when a create flow reopens with a server-saved draft.
 * Previews the recovered note and lets the user continue editing or discard it.
 */
const NoteRecoveryDialog = ({
  open,
  draftText,
  onContinue,
  onDiscard,
}: NoteRecoveryDialogProps): ReactElement => {
  // No `onClose`: the user must explicitly choose to continue or discard.
  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography variant="titleLarge">Note recovered</Typography>
      </DialogTitle>
      <DialogContent>
        <FlexContainer
          direction="column"
          alignItems="center"
          gap={2}
          height="fit-content"
        >
          <IconAvatar avatarType="notepad" size={60} weight="regular" />
          <Typography variant="bodyMedium" align="center">
            Your note from your last session is ready to pick up where you left
            off.
          </Typography>
          <NotePreviewCard text={draftText} />
        </FlexContainer>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onDiscard}
          trackingName="note-draft-discard"
        >
          Discard note
        </Button>
        <Button
          variant="contained"
          onClick={onContinue}
          trackingName="note-draft-continue"
        >
          Open recovered note
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteRecoveryDialog;
